import * as _ from 'lodash';

import { Attribute } from 'src/app/shared/models/attributes/attribute';
import { AttributeDefinition } from 'src/app/shared/models/attributes/attributeDefinition';
import { Observable, BehaviorSubject } from 'rxjs';
import { AttributeWrite } from 'src/app/shared/models/attributes/attributeWrite';
import { AttributeMultiValueWrite } from 'src/app/shared/models/attributes/attributeMultiValueWrite';
import { InjectorModule } from 'src/app/shared/modules/injector.module';
import { AttributeService } from 'src/app/shared/services/attribute.service';
import { EditableAttribute } from 'src/app/shared/models/attributes/editableAttribute';
import { FlatAttribute } from '../../models/attributes/flatAttribute';
import { AttributeUiType } from '../../models/attributes/attributeUiType';
import {
  ComplexAttributeWrite,
  ComplexSubFieldValuesWrite,
} from '../../models/attributes/complexAttributeWrite';
import { MemberAttributeWrite } from '../../models/attributes/memberAttributeWrite';

export class AttributesEditorDataSource {
  private _attributes: Array<Attribute> = new Array<Attribute>();
  private _attributeDefinitions: Array<AttributeDefinition> = new Array<AttributeDefinition>();
  private _filter: (a: Array<Attribute>) => Array<Attribute> =
    AttributesEditorDataSource.defaultFilter;

  protected _editable: BehaviorSubject<EditableAttribute[]> = new BehaviorSubject<
    EditableAttribute[]
  >(null);
  public editableAttributes: Observable<EditableAttribute[]> = this._editable.asObservable();

  public sortOnName: boolean = true;

  get attributes(): Array<Attribute> {
    return this._attributes;
  }
  get attributeDefinitions(): Array<AttributeDefinition> {
    return this._attributeDefinitions;
  }
  get editable(): Array<EditableAttribute> {
    return this._editable.value;
  }
  get hasAttributes(): boolean {
    return this.editable && this.editable.length > 0;
  }
  get requiresValidation(): boolean {
    if (!this.hasAttributes) return false;
    let hasRequired = _.some(this.editable, (attribute) => attribute.required);
    let hasRanges = _.some(this.editable, (attribute) => attribute.needsRangeCheck);
    let hasDateRange = _.some(this.editable, (attribute) => attribute.needsDateRangeCheck);
    let hasDate = _.some(this.editable, (attribute) => attribute.needsDateCheck);
    let hasRangeComplex = _.some(this.editable, (attribute) => attribute.needsRangeCheckComplex);
    return hasRequired || hasRanges || hasDateRange || hasDate || hasRangeComplex;
  }
  get profileRequiresValidation(): boolean {
    if (!this.hasAttributes) return false;
    let hasRequired = _.some(this.editable, (attribute) => attribute.required);
    let hasRanges = _.some(this.editable, (attribute) => attribute.needsRangeCheck);
    let hasDateRange = _.some(this.editable, (attribute) => attribute.needsDateRangeCheck);
    let hasDate = _.some(this.editable, (attribute) => attribute.needsDateCheck);
    return hasRequired || hasRanges || hasDateRange || hasDate;
  }
  get isValid(): boolean {
    if (!this.hasAttributes) {
      return true;
    }

    if (!this.requiresValidation) {
      return true;
    }

    return _.chain(this.editable)
      .every((attribute) => attribute.isValid)
      .value();
  }

  get profileisValid(): boolean {
    if (!this.hasAttributes) {
      return true;
    }

    if (!this.profileRequiresValidation) {
      return true;
    }

    return _.chain(this.editable)
      .every((attribute) => attribute.profileisValid)
      .value();
  }

  public load(
    attributes: Array<Attribute>,
    attributeDefinitions: Array<AttributeDefinition>,
    filter: (a: Array<Attribute>) => Array<Attribute> = AttributesEditorDataSource.defaultFilter
  ) {
    this._filter = filter;
    this._attributes = filter(attributes);
    this._attributeDefinitions = attributeDefinitions;
    this.buildEditable();
  }

  public getWriteAttributes = (): MemberAttributeWrite => {
    if (this.hasAttributes) {
      const attributes = this.asAttributeWrites();
      const complexAttributes = this.asComplexAttributeWrites();
      return {
        attributes,
        complexAttributes: complexAttributes,
      };
    }
    return {
      attributes: [],
      complexAttributes: [],
    };
  };

  public asAttributeWrites(): Array<AttributeWrite> {
    const grouped = _.groupBy(
      this.editable?.filter(
        (x) => x.isVisible && !x.isReadOnly && !x.isHidden && x.type !== AttributeUiType.Complex
      ),
      'attributeDefinitionId'
    );
    const attributeWrites = _.map(grouped, function (attributes: Attribute[]) {
      return AttributeWrite.fromAttributes(attributes);
    });

    return _.reject(attributeWrites, function (attribute) {
      return _.isUndefined(attribute);
    });
  }

  public asComplexAttributeWrites(): ComplexAttributeWrite[][] {
    const grouped = _.groupBy(
      this.editable?.filter(
        (x) => x.isVisible && !x.isReadOnly && !x.isHidden && x.type === AttributeUiType.Complex
      ),
      'attributeDefinitionId'
    );
    return _.map(grouped, function (attributes: EditableAttribute[]) {
      let result: ComplexAttributeWrite[] = [];
      attributes.forEach((attribute) => {
        let complexAttributeWrites: ComplexAttributeWrite[] = [];
        attribute.complexAttributeValues.forEach((item) => {
          let complexAttributeWrite = new ComplexAttributeWrite();
          complexAttributeWrite.attributeDefinitionId = attribute.attributeDefinitionId;
          complexAttributeWrite.complexSubFieldValues = item.items.map((x) => {
            let subFieldDefinition = attribute.subFieldList.find(
              (sub) => sub.id == x.subFieldDefinitionItemId
            ).subFieldDefinition;
            if (x.isCheckboxType || x.isMultiSelectType) {
              return {
                subFieldDefinitionItemId: x.subFieldDefinitionItemId,
                subFieldItemValue: {
                  attributeDefinitionId: subFieldDefinition.id,
                  attributeDefinitionValueListItemId: null,
                  values: x.subFieldValues.map((subValue) => {
                    return {
                      added: false,
                      attributeDefinitionValueListItemId:
                        subValue.attributeDefinitionValueListItem.id,
                      attributeDefinitionValueListItemValue:
                        subValue.attributeDefinitionValueListItem.itemValue,
                    };
                  }),
                  value: null,
                  entityType: subFieldDefinition.entityType,
                  uiType: subFieldDefinition.uiType,
                },
              } as ComplexSubFieldValuesWrite;
            } else {
              return {
                subFieldDefinitionItemId: x.subFieldDefinitionItemId,
                subFieldItemValue: {
                  attributeDefinitionId: subFieldDefinition.id,
                  attributeDefinitionValueListItemId:
                    x.subFieldValues[0]?.attributeDefinitionValueListItem?.id,
                  values: null,
                  value: x.rawValue,
                  entityType: subFieldDefinition.entityType,
                  uiType: subFieldDefinition.uiType,
                },
              } as ComplexSubFieldValuesWrite;
            }
          });
          complexAttributeWrites.push(complexAttributeWrite);
        });
        result = result.concat(complexAttributeWrites);
      });
      return result;
    });
  }
  private attributesOfInterest(): Array<Attribute> {
    return _.chain(this._attributes)
      .filter((attr) =>
        _.some(
          this._attributeDefinitions,
          (attrDefinition) => attrDefinition.id == attr.attributeDefinitionId
        )
      )
      .value();
  }

  private buildEditable() {
    let filteredAndCloned = _.cloneDeep(this.attributesOfInterest());
    let editableAttributes = new Array<EditableAttribute>();
    _.forEach(this._attributeDefinitions, (attributeDefinition) => {
      if (attributeDefinition.uiType === AttributeUiType.Complex) {
        let complexValues = _.filter(
          filteredAndCloned,
          (attr) => attr.attributeDefinitionId == attributeDefinition.id
        ).map((attr) => attr.complexAttributeSubFieldValue);

        let attribute = EditableAttribute.fromAttributeDefinition(attributeDefinition);
        attribute.complexAttributeValues = FlatAttribute.flatComplexValues(complexValues);
        editableAttributes.push(attribute);
      } else {
        if (attributeDefinition.allowsMultipleValues) {
          let values = _.filter(
            filteredAndCloned,
            (attr) => attr.attributeDefinitionId == attributeDefinition.id
          );
          let attribute = EditableAttribute.fromAttributeDefinition(attributeDefinition);
          attribute.valueList = _.map(values, (attr) => attr.attributeDefinitionValueListItem);
          editableAttributes.push(attribute);
        } else {
          let value = _.find(
            filteredAndCloned,
            (attr) => attr.attributeDefinitionId == attributeDefinition.id
          );
          let editable = value ? new EditableAttribute(value, attributeDefinition) : null;
          editableAttributes.push(
            editable || EditableAttribute.fromAttributeDefinition(attributeDefinition)
          );
        }
      }
    });

    let filtered = <Array<EditableAttribute>>(
      this._filter(
        this.sortOnName === true ? _.orderBy(editableAttributes, 'name') : editableAttributes
      )
    );

    this._editable.next(filtered);
  }

  /// This block of code should be refactored as
  /// the work done here should not be the responsibility
  /// of this component.
  private refreshSearchFilters(response: Attribute[], updatedAttributes) {
    let attributesToUpdate = [];

    _.each(updatedAttributes, (addedAttributeWrite: AttributeWrite) => {
      let addedValues = _.filter(addedAttributeWrite.values, { added: true });
      _.each(addedValues, (addedValue: AttributeMultiValueWrite) => {
        let attribute = _.find(response, (value: Attribute) => {
          return (
            value.attributeDefinitionId === addedAttributeWrite.attributeDefinitionId &&
            value.value === addedValue.attributeDefinitionValueListItemValue
          );
        });
        attributesToUpdate.push(attribute);
      });
    });
    if (attributesToUpdate && attributesToUpdate.length > 0) {
      let attrService = InjectorModule.injector.get(AttributeService);
      attrService.updateAttributes(attributesToUpdate);
    }
  }

  public static defaultFilter(attributes: Array<Attribute>): Array<Attribute> {
    return _.filter(attributes, function (attr) {
      return (
        attr.isShortAnswerType() ||
        attr.isNumberType() ||
        attr.isDateType() ||
        attr.isLongAnswerType() ||
        attr.isDropDownType() ||
        attr.isCheckboxType() ||
        attr.isMultiSelectType() ||
        attr.isSkillType() ||
        attr.isStarRatingType() ||
        attr.isDateRangeType() ||
        attr.isDocumentType() ||
        attr.isComplexType() ||
        attr.isEncryptedFieldType()
      );
    });
  }

  public asComplexAttributeWrite(attribute: EditableAttribute): ComplexAttributeWrite {
    let values = this.asAttributeWrites();
    if (values.length != 0) {
      let complexFieldWrite: ComplexAttributeWrite = {
        attributeDefinitionId: attribute.attributeDefinitionId,
        complexSubFieldValues: values.map((v) => {
          return {
            subFieldDefinitionItemId: attribute.subFieldList.find(
              (x) => x.subFieldDefinitionId == v.attributeDefinitionId
            ).id,
            subFieldItemValue: v,
          };
        }),
      };
      return complexFieldWrite;
    } else {
      return null;
    }
  }
}
